import React from 'react';
import { Form } from 'antd';

import { Tooltip } from 'app/components/common/tooltip';
import { i18n } from 'app/i18n';
import { Typography } from 'app/components/domain/typography';
import { Image } from 'app/components/common/image';
import help from 'app/resources/help.svg';

import styles from './input-field-wrapper.module.scss';

type ValidateStatus = 'success' | 'warning' | 'error' | 'validating' | '';

export interface FieldProps {
    value?: any;
}

export interface FieldMetaProps {
    active?: boolean;
    dirty: boolean;
    error?: any;
    invalid: boolean;
    pristine: boolean;
    submitting: boolean;
    touched: boolean;
    valid: boolean;
    visited: boolean;
    warning?: any;
    initial?: any;
}

interface TooltipProps {
    title: string;
    text: string;
}

interface OwnProps<T extends FieldProps> {
    label?: string;
    required?: boolean;
    tooltip?: TooltipProps;
    disabled?: boolean;
    metadata: FieldMetaProps;
    component: React.ComponentType<T>;
    input: T;
}

interface State {
    isTooltipOpen?: boolean;
}

export type FieldWrapperProps<T extends FieldProps> = OwnProps<T>;

const validateStatus = (metadata: FieldMetaProps): ValidateStatus => {
    const { touched, error, warning, valid } = metadata;

    if (touched) {
        if (error) {
            return 'error';
        }
        if (warning) {
            return 'warning';
        }
        if (valid) {
            return 'success';
        }
    }
    return '';
};

const validateMessage = (metadata: FieldMetaProps): any => {
    const { touched, error, warning } = metadata;

    return touched && (error || warning);
};

class InputFieldWrapper<T extends FieldProps> extends React.Component<FieldWrapperProps<T>, State> {
    public readonly state: State = {
        isTooltipOpen: false,
    };

    public render(): React.ReactNode {
        const {
            label,
            metadata,
            tooltip,
            metadata: { submitting },
            component: Component,
            required,
            disabled,
            input: { value },
            input,
            ...rest
        } = this.props;

        const { isTooltipOpen } = this.state;

        const renderLabel = (
            <div className={styles.inputLabelContainer} onClick={this.handleUnwantedBehavior}>
                <Typography variant="body1" component="p" className={styles.title}>
                    {label}
                </Typography>
                {tooltip && (
                    <button className={styles.tooltipButton} onClick={this.handleToggleOpen}>
                        <Image src={help} alt={i18n.image.help} className={styles.helpImage} />
                    </button>
                )}
            </div>
        );

        return (
            <Form.Item
                required={required}
                label={renderLabel}
                validateStatus={validateStatus(metadata)}
                help={validateMessage(metadata)}
            >
                {tooltip && isTooltipOpen && (
                    <Tooltip title={tooltip.title} text={tooltip.text} onClose={this.handleClose} />
                )}
                <Component {...input} {...rest} disabled={disabled || submitting} value={value} />
            </Form.Item>
        );
    }

    private handleToggleOpen = () => {
        this.setState((prevState) => ({ isTooltipOpen: !prevState.isTooltipOpen }));
    };

    private handleClose = () => {
        this.setState({ isTooltipOpen: false });
    };

    private handleUnwantedBehavior = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };
}

export { InputFieldWrapper };
